import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  ShoppingBag, 
  Image, 
  MessageSquare, 
  Users, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  DollarSign,
  Building2,
  Briefcase,
  Settings,
  Search,
  Layers
} from 'lucide-react';
import SiteSelector from './SiteSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = () => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/blog', label: 'Blog Posts', icon: FileText, count: stats?.blog_posts },
    { path: '/admin/products', label: 'Products', icon: ShoppingBag, count: stats?.products },
    { path: '/admin/services', label: 'Services', icon: Briefcase, count: stats?.services },
    { path: '/admin/lanes', label: 'Three Lanes', icon: Layers },
    { path: '/admin/images', label: 'Images', icon: Image, count: stats?.images },
    { path: '/admin/orders', label: 'Orders', icon: DollarSign, count: stats?.orders },
    { path: '/admin/contacts', label: 'Contacts', icon: MessageSquare, count: stats?.new_contacts },
    { path: '/admin/subscribers', label: 'Subscribers', icon: Users, count: stats?.subscribers },
    { path: '/admin/seo', label: 'SEO & Settings', icon: Search },
  ];

  // Add sites management for super admins
  if (stats?.is_super_admin) {
    navItems.push({ path: '/admin/sites', label: 'Sites', icon: Building2, count: stats?.site_count });
  }

  const handleSiteChange = () => {
    fetchStats();
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-stone-900 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-stone-800 transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-stone-700">
            <Link to="/" className="block mb-4">
              <h1 className="font-serif text-xl text-white">Margaret Núñez</h1>
              <p className="text-xs text-terracota tracking-wider uppercase mt-1">Admin Panel</p>
            </Link>
            <SiteSelector onSiteChange={handleSiteChange} />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center justify-between px-4 py-3 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-terracota text-white' 
                      : 'text-stone-400 hover:bg-stone-700 hover:text-white'
                    }
                  `}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                  {item.count !== undefined && item.count > 0 && (
                    <span className={`
                      text-xs px-2 py-0.5 rounded-full
                      ${isActive ? 'bg-white/20' : 'bg-stone-700'}
                    `}>
                      {item.count}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-stone-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-terracota rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-white text-sm font-medium">{user?.username}</p>
                <p className="text-stone-500 text-xs">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-stone-400 hover:text-red-400 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-stone-800 border-b border-stone-700 px-4 py-4 lg:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-stone-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-2 text-sm text-stone-400">
              <Link to="/" className="hover:text-terracota transition-colors">
                View Site
              </Link>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet context={{ stats, refreshStats: fetchStats }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
